/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  AbstractFormComponent, BuilderNavigationService, BuilderUserContext, CommonActions, FormActionPipelines, FormFlowState, PrinterBusinessService, salesManagementReportFilterDialogRoute, whenFormInvalid, } from '@gh/builder-shared';
import {
  AelEvents, BuilderPermission, EomReportMethod, EomReportsHistory, SystemRoleBit, } from '@gh/builder-shared-data';
import { ActionFlow, EnumDictionary, getRouteInitData } from '@gh/common-shared';
import { ID, isProduceStatusCompleted, isProduceStatusInProgress } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DialogService,
  NotificationService,
  TextMessageDialogConfig,
  WaitingService,
} from '@gh/core-ui';
import { createLink, currentDate, getMonthLogicalName, toString } from '@gh/core-util';
import { of$, throwError$ } from '@gh/rx';
import { map$, catchError$, switchMap$ } from '@gh/rx/operators';
import { omit } from 'lodash';
import { noop, Observable, Subscription } from 'rxjs';
import { EomReport, EomReportBusinessService } from '../../resources';
import { EomReportDetailsFormStateService } from './eom-report-details-form-state.service';
import { textMessageDialogRoute } from '@gh/common-internal-shared';
import { Validators } from '@angular/forms';
import { GoogleAuthService } from '@gh/core-external';

@Component({
  standalone: false,
  selector: 'gh-eom-report-details',
  templateUrl: 'eom-report-details.component.html',
  styleUrls: ['eom-report-details.component.scss'],
  viewProviders: [
    EomReportDetailsFormStateService,
  ],
})
export class EomReportDetailsComponent extends AbstractFormComponent<EomReport> implements OnInit, OnDestroy {
  eomMethodDict: EnumDictionary<EomReportMethod>;
  eomHistory?: EomReportsHistory;
  isCloud: boolean;
  visibleWipIcon: boolean;
  eomHistoryDate: any;
  buildersInsuranceVisible: boolean;
  private produceSubscription: Subscription;

  constructor(
    public form: EomReportDetailsFormStateService,
    public userContext: BuilderUserContext,
    private route: ActivatedRoute,
    private navigations: BuilderNavigationService,
    private pipelines: FormActionPipelines,
    private commonActions: CommonActions,
    private businessService: EomReportBusinessService,
    private translations: TranslationService,
    private notifications: NotificationService,
    private dialogService: DialogService,
    private googleAuth: GoogleAuthService,
    private printerService: PrinterBusinessService,
    private waitings: WaitingService,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    const { eomReport, eomMethodDict, printData } = getRouteInitData(this.route);
    const id = localStorage.getItem('eomTaskId');
    this.visibleWipIcon = eomReport.displayWip;
    this.buildersInsuranceVisible = eomReport.buildersInsuranceEnabled;
    if (!id) {
      eomReport.produceStatus = { total: null, processed: null };
    } else {
      this.businessService.getProduceStatus(id).subscribe((status) => this.form.setProduceStatus(status));
    }
    this.eomMethodDict = eomMethodDict;
    this.isCloud = !!printData.isCloud;
    this.form.build(eomReport);
    this.setEomHistory(eomReport);
    this.onWhsChange();
  }

  ngOnDestroy(): void {
    localStorage.removeItem('eomTaskId');
    if (this.produceSubscription) {
      this.produceSubscription.unsubscribe();
    }
  }

  get producingLabel(): string {
    const { produceStatus } = this.form;
    return produceStatus ? this.translations.get('lbl_eom_reports_in_producing', {
      totalProduced: toString(produceStatus.processed),
      totalAll: toString(produceStatus.total),
    }) : '';
  }

  get completeFlow(): ActionFlow<FormFlowState<EomReport>> {
    return this.pipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, () => this.handleErrors()),
      execute: this.commonActions.async(
        (state: FormFlowState<EomReport>) => {
          if (this.isCloud) {
            const value = omit(state.form.submitValue, ['printer']);
            const authCode$ = this.form.isAuthCodeRequired ? this.printerService.grantGoogleOfflineAccess() : of$(true);
            return authCode$.pipe(
              switchMap$((isConnected: boolean) =>
                this.businessService.save({ ...value, authCode: this.googleAuth.accessToken } as EomReport)),
              map$(() => state));
          } else {
            const authCode$ = of$('');
            return authCode$.pipe(
              switchMap$((authCode) => this.businessService.save(this.form.submitValue)),
              map$(() => state));
          }
        },
      ),
      finish: ({ value }) => {
        const id = localStorage.getItem('eomTaskId') || '';
        this.form.build(value);
        this.onWhsChange();
        this.form.get('whs').markAsDirty();
        this.notifications.success('msg_eom_produce_tasks_added');
        this.processStatus(id);
      },
    });
  }

  get statusProduceCompleted(): boolean {
    return isProduceStatusCompleted(this.form.produceStatus);
  }

  get wipVisible(): boolean {
    return this.visibleWipIcon;
  }

  get goToReportEnabled(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Reports.Sales.SALES_MANAGEMENT);
  }

  goToAel(): void {
    this.navigations.goToAel({
      label: 'lbl_eom_reports',
      entityId: this.userContext.office!.id,
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.EomReportsEdited],
    });
  }

  getMonthName(month: number): string {
    const monthName = getMonthLogicalName(month - 1);
    return this.translations.get(`lbl_${monthName}`);
  }

  setEomHistory(report: EomReport): void {
    const now = currentDate();
    const month = report.forMonth || now.getMonth();
    const year = report.forYear || now.getFullYear();

    this.eomHistory = report.historyEomSettings
      .find((item: EomReportsHistory) => item.forMonth === month && item.forYear === year);

    if (!this.eomHistory) {
      this.eomHistory = {
        id: '',
        forMonth: month,
        forYear: year,
        whs: false,
      };

      report.historyEomSettings.push(this.eomHistory);
    }

    this.form.updateHistory(this.eomHistory);

    const currentMonth = getMonthLogicalName(month - 1);
    this.eomHistoryDate = {
      month: currentMonth[0].toUpperCase() + currentMonth.slice(1),
      year,
    };
  }

  updateEomHistory(item: EomReportsHistory): void {
    this.form.updateHistory(item);
    this.form.uncheckAllCheckboxes();
    this.businessService
      .validate(item.forYear, item.forMonth)
      .pipe(this.waitings.operator(),
        catchError$((error) => {
          this.visibleWipIcon = true;
          return throwError$(error);
        }),
      )
      .subscribe((res) => {
        this.visibleWipIcon = res.displayWip;
      },
        this.notifications.processAll);

    const currentMonth = getMonthLogicalName(item.forMonth - 1);
    this.eomHistoryDate = {
      month: currentMonth[0].toUpperCase() + currentMonth.slice(1),
      year: item.forYear,
    };
    this.onWhsChange();
  }

  updateEomMethod(): void {
    this.form.updateMethod(this.isCloud);
  }

  openFindPrinterDialog(): void {
    this.printerService
      .openPrinterFinder({
        title: this.translations.get('lbl_select_printer'),
        preselected: this.form.printerId ? [this.form.printerId] : void 0,
      })
      .subscribe(
        (printer) => this.form.updatePrinter(printer),
        this.notifications.processAll,
      );
  }

  goToReport(): void {
    this.dialogService.openWithInput(salesManagementReportFilterDialogRoute, {
      forYear: this.form.getForYearControlValue(),
      forMonth: this.form.getForMonthControlValue(),
    });
  }

  goToWip(): void {
    this.router.navigate(['/manager/wip', { month: this.eomHistoryDate.month, year: this.eomHistoryDate.year }]);
  }

  onWhsChange(): void {
    if (this.form.getWhs()) {
      this.form.getControl('whsDetails').setValidators(Validators.required);
    } else {
      this.form.getControl('whsDetails').clearValidators();
      this.form.getControl('whsDetails').updateValueAndValidity();
    }
    if (this.eomHistory) {
      this.eomHistory.whs = this.form.getWhs();
    }
  }

  onWhsDetailsChange(): void {
    if (this.eomHistory) {
      this.eomHistory.whsDetails = this.form.getWhsDetails();
    }
  }

  processStatus(id: ID): void {
    this.businessService.getProduceStatus(id).subscribe((status) => {
      this.form.setProduceStatus(status);
      if (!this.isCloud && !isProduceStatusInProgress(status)) {
        localStorage.removeItem('eomTaskId');
        this.businessService.gets3Link(id).pipe(
          this.waitings.operator(),
          switchMap$((report) => report.s3Link && this.form.getControl('method').value ===
            EomReportMethod.PrintedOnSelectedPrinter ? this.showReportDialog(report) : of$('')))
          .subscribe({ next: noop, error: this.notifications.processAll });
      } else {
        setTimeout(() => this.processStatus(id), 2000);
      }
    }, this.processError);
  }

  processError(error: any): void {
    this.form.enable();
    this.notifications.processAll(error);
    this.form.setProduceStatus({ total: 0, processed: 0 });
  }

  private handleErrors(): void {
    this.form.get('whsDetails').markAsTouched();
    this.notifications.error('msg_eom_whs_empty');
  }

  private showReportDialog(report: any): Observable<void> {
    return this.dialogService.openWithInput(textMessageDialogRoute, <TextMessageDialogConfig<string>>{
      title: this.translations.get('Link to Print EoM Reports'),
      html: [
        this.translations.get('Please follow the link below to print EoM Reports'),
        createLink(report.s3Link, 'Your file'),
      ].join(' '),
      buttons: [{ value: 'ok', title: this.translations.get('lbl_ok'), type: 'primary' }],
    }).afterClosed();
  }
}
